import { baseApi } from 'store/api/base-api';
import { Project, ProjectResponse } from 'types/project';
import { transformProjectResponse } from './utils/transformProjectResponse';
import { chatApi } from '../chat/chat-api';
import { formatFile } from 'lib/utils/common';

export const projectApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProjects: builder.query<Project[], void>({
      query: () => ({ url: 'projects', method: 'GET' }),
      transformResponse(
        response: { projects: ProjectResponse[] },
        _meta,
        _arg,
      ) {
        const { projects } = response;
        return projects.map(transformProjectResponse);
      },
      providesTags: ['Project'],
    }),

    createProject: builder.mutation<
      Project,
      { name: string; instruction: string; files: File[] }
    >({
      async queryFn(body, api, _extraOptions, baseQuery) {
        const files = await Promise.all(
          body.files.map((file) => formatFile(file)),
        );

        const response = await baseQuery({
          url: 'project',
          method: 'POST',
          body: { ...body, files },
        });

        if (response.error) return { error: response.error };

        const { project: projectResponse } = response.data as {
          project: ProjectResponse;
        };

        const project = transformProjectResponse(projectResponse);

        api.dispatch(
          projectApi.util.updateQueryData('getProjects', undefined, (draft) => {
            draft.push(project);
          }),
        );

        return { data: project };
      },
    }),

    updateProject: builder.mutation<
      Project,
      {
        projectId: Project['id'];
        name: string;
        instruction: string;
        newFiles: File[];
        removedFileIds: string[];
      }
    >({
      async queryFn(body, api, _extraOptions, baseQuery) {
        const newFiles = await Promise.all(
          body.newFiles.map((file) => formatFile(file)),
        );

        const response = await baseQuery({
          url: 'api/project',
          method: 'PATCH',
          body: { ...body, newFiles },
        });

        if (response.error) return { error: response.error };

        const { project: projectResponse } = response.data as {
          project: ProjectResponse;
        };

        const project = transformProjectResponse(projectResponse);

        api.dispatch(
          projectApi.util.updateQueryData('getProjects', undefined, (draft) => {
            const updatedProject = draft.find(
              (item) => item.id === body.projectId,
            );
            if (updatedProject) {
              updatedProject.name = project.name;
              updatedProject.instruction = project.instruction;
              updatedProject.files = project.files;
            }
          }),
        );

        return { data: project };
      },
    }),

    deleteProject: builder.mutation<void, Project['id']>({
      query: (projectId) => ({
        url: 'project/delete',
        method: 'POST',
        body: { projectId },
      }),
      async onQueryStarted(projectId, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;

          const state = getState();
          const allChats =
            chatApi.endpoints.getChats.select(undefined)(state).data || [];
          const chatIdsToUpdate = allChats
            .filter((chat) => chat.projectId === projectId)
            .map((chat) => chat.id);

          chatIdsToUpdate.forEach((chatId) => {
            dispatch(
              chatApi.util.updateQueryData('getChat', chatId, (draft) => {
                if (draft) draft.projectId = null;
              }),
            );
          });

          dispatch(
            chatApi.util.updateQueryData('getChats', undefined, (draft) => {
              draft.forEach((chat) => {
                if (chat.projectId === projectId) chat.projectId = null;
              });
            }),
          );

          dispatch(
            projectApi.util.updateQueryData(
              'getProjects',
              undefined,
              (draft) => {
                return draft.filter((project) => project.id !== projectId);
              },
            ),
          );
        } catch {
          // console.log('Ошибка удаления проекта...', err);
        }
      },
    }),

    renameProject: builder.mutation<
      { success: boolean; project: ProjectResponse },
      { projectId: Project['id']; name: string }
    >({
      query: ({ projectId, name }) => ({
        url: `project/rename`,
        method: 'POST',
        body: { projectId, name },
      }),
      async onQueryStarted({ projectId, name }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            projectApi.util.updateQueryData(
              'getProjects',
              undefined,
              (draft) => {
                const targetProject = draft.find(
                  (item) => item.id === projectId,
                );
                if (targetProject) {
                  targetProject.name = name;
                }
              },
            ),
          );
        } catch {
          // console.log('Rename error:', err);
        }
      },
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useRenameProjectMutation,
} = projectApi;
